import { inject, Injectable } from '@angular/core';
import { MenuService } from 'app';
import { AuthService } from 'auth';
import { DrFutureDnaReportStatus } from './dr-future.service';

/**
 * The AI assistants of the Global Health member area (customer order 2026-10-09).
 *
 * Dr. Future is the general companion; NutriGuide, BodyIQ and BioTune are the
 * DNA-based assistants, each its own provider behind the same Dr. Future API
 * (`?assistant=<key>`). The PRODUCT NAMES are language-independent and not
 * translated — they are the customer's working names until confirmed, so they
 * live here and only here. Descriptions and notices are in `dr_future.assistants.*`.
 */
export type DrFutureAssistantKey = 'dr_future' | 'nutriguide' | 'bodyiq' | 'biotune';

export interface DrFutureAssistantDef {
  key: DrFutureAssistantKey;
  /** Product name, shown as-is in every language. */
  name: string;
  /** URL segment under /:lang/dr-future (empty for Dr. Future itself). */
  slug: string;
  /** Registered CoreUI icon (libs/app IconService), camelCase for `cIcon`. */
  icon: string;
  /** Kebab-case of the same icon for the sidebar (`iconComponent.name`). */
  menuIcon: string;
  /** Needs a finished DNA evaluation (backend: 403 dna_report_required otherwise). */
  dnaGated: boolean;
}

export const DR_FUTURE_ASSISTANTS: Readonly<Record<DrFutureAssistantKey, DrFutureAssistantDef>> = {
  dr_future:  { key: 'dr_future',  name: 'Dr. Future', slug: '',           icon: 'cilCompass',    menuIcon: 'cil-compass',    dnaGated: false },
  nutriguide: { key: 'nutriguide', name: 'NutriGuide', slug: 'nutriguide', icon: 'cilRestaurant', menuIcon: 'cil-restaurant', dnaGated: true },
  bodyiq:     { key: 'bodyiq',     name: 'BodyIQ',     slug: 'bodyiq',     icon: 'cilRunning',    menuIcon: 'cil-running',    dnaGated: true },
  biotune:    { key: 'biotune',    name: 'BioTune',    slug: 'biotune',    icon: 'cilBeaker',     menuIcon: 'cil-beaker',     dnaGated: true },
};

/** The DNA-based assistants, in menu order (directly under "Ask Dr. Future"). */
export const DNA_ASSISTANTS: ReadonlyArray<DrFutureAssistantDef> = [
  DR_FUTURE_ASSISTANTS.nutriguide,
  DR_FUTURE_ASSISTANTS.bodyiq,
  DR_FUTURE_ASSISTANTS.biotune,
];

export function isDrFutureAssistantKey(value: string | null | undefined): value is DrFutureAssistantKey {
  return !!value && Object.prototype.hasOwnProperty.call(DR_FUTURE_ASSISTANTS, value);
}

/** The DNA offer page: the static marketing page on the same host, no language prefix. */
export function dnaOfferUrl(): string {
  return `${window.location.origin}/dna-health-special`;
}

/**
 * Lock badge on the three DNA assistants in the sidebar while the member's DNA
 * evaluation is not ready. The entries themselves are always visible (never
 * greyed out, never hidden) — the badge is only a hint; the page shows the
 * notice and the backend enforces the lock.
 *
 * Fed from the auth user (`dna_report_status` in user_payload) and corrected by
 * the assistant page whenever `GET /dr_future/assistants` answers, because that
 * is the authority and the cached user may be older than the evaluation.
 * The badge text is a symbol so it survives a language switch (dynamic badges
 * are re-applied by the shared MenuService without re-translation).
 */
@Injectable({ providedIn: 'root' })
export class DrFutureAssistantBadgeService {
  private menuService = inject(MenuService);
  private authService = inject(AuthService);

  private static readonly LOCK_BADGE = { color: 'secondary', text: '🔒' };

  initialize(): void {
    this.authService.getUser$().subscribe(user => {
      this.apply(user ? (user.dna_report_status ?? 'none') : null);
    });
  }

  /** `null` (signed out) clears the badges. */
  apply(status: DrFutureDnaReportStatus | null): void {
    const locked = status !== null && status !== 'ready';
    DNA_ASSISTANTS.forEach(a =>
      this.menuService.updateMenuBadge(`/dr-future/${a.slug}`, locked ? DrFutureAssistantBadgeService.LOCK_BADGE : undefined));
  }
}
